import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Grid,
  Chip,
  Paper,
  Button,
  Dialog,
  Divider,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { Add, Edit, Delete, Sync, AssignmentTurnedIn } from "@material-ui/icons";
import { Alert, Autocomplete } from "@material-ui/lab";

import toJs from "../../hoc/toJS";
import select from "../../utils/select";
import { getAllProfiles } from "../../reducer/department/action";
import { uploadFile } from "../../reducer/file/action";
import FilePreviewModal from "../../containers/File/FilePreviewModal";
import ScientificResearchUpdateModal from "../../containers/ScientificResearch/ScientificResearchUpdateModal";
import {
  getAllContract,
  getContractById,
  insertContract,
  updateContract,
  deleteContract,
  addAcceptance,
  deleteAcceptance,
  getAvailablePapers,
  getWarnings,
} from "../../reducer/raContract/action";

// [VinUni] Module 6 - Hợp đồng TLNC (RA)

// [VinUni] Map type của ScientificResearch -> filter mà ScientificResearchUpdateModal dùng để render đúng sub-form
const paperFilterFromType = (type) => {
  switch (type) {
    case "scientificResearchJournal":
      return "journalInternational";
    case "scientificResearchJournalDomestic":
      return "journalDomestic";
    case "scientificResearchConference":
      return "conference";
    case "scientificResearchConferenceDomestic":
      return "conferenceDomestic";
    case "chapterJournal":
    case "chapterConference":
    case "chapterJournalDomestic":
    case "chapterConferenceDomestic":
      return "chapter";
    case "working":
      return "workingPaper";
    case "scientificResearchOther":
      return "other";
    default:
      return "journalInternational";
  }
};

// [VinUni] Chuẩn hoá dữ liệu bài báo về đúng shape mà ScientificResearchUpdateModal cần (updating)
// resp: có thể là ScientificResearchResponse {scientificResearch, journal, journalDomestic, conference}
//       hoặc bản ghi acceptance {scientificResearch} (bài đã gán - preload từ BE).
const buildPaperUpdating = (resp) => {
  if (!resp) return null;
  const sr = resp.scientificResearch || resp;
  if (!sr) return null;
  const publication = resp.journal || resp.journalDomestic || resp.conference || undefined;
  return {
    ...sr,
    publication,
    filter: paperFilterFromType(sr.type),
    submissionYear: sr.submissionDay ? new Date(sr.submissionDay).getFullYear() : undefined,
  };
};

const PRODUCT_TYPES = [
  { value: "scientificResearch", label: "Bài báo khoa học" },
  { value: "monograph", label: "Sách chuyên khảo" },
  { value: "solution", label: "Giải pháp" },
  { value: "patent", label: "Sáng chế" },
  { value: "researchProposal", label: "Đề xuất nghiên cứu" },
  { value: "other", label: "Khác" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Bản nháp" },
  { value: "active", label: "Đang hiệu lực" },
  { value: "completed", label: "Đã nghiệm thu" },
  { value: "expired", label: "Hết hạn" },
  { value: "cancelled", label: "Đã huỷ" },
];

const emptyForm = {
  id: null,
  code: "",
  title: "",
  profileId: "",
  startDate: "",
  endDate: "",
  acceptanceDueDate: "",
  status: "draft",
  note: "",
  deliverables: [],
};

const productTypeLabel = (v) => PRODUCT_TYPES.find((t) => t.value === v)?.label || v || "";
const statusLabel = (v) => STATUS_OPTIONS.find((s) => s.value === v)?.label || v || "";

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("vi-VN");
};
const toInputDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};
const toIso = (v) => (v ? new Date(v).toISOString() : null);

const acceptanceProgress = (contract) => {
  const required = (contract.deliverables || []).reduce((sum, d) => sum + (Number(d.requiredQuantity) || 0), 0);
  const done = (contract.acceptances || []).length;
  return `${done}/${required}`;
};

const evidenceLink = (url) => {
  if (!url) return "";
  if (url.startsWith("/storage")) return `${process.env.REACT_APP_DOMAIN}/api/v1${url}`;
  return url;
};

function RaContract(props) {
  const {
    role,
    contracts,
    detail,
    availablePapers,
    warnings,
    profiles,
    isFetching,
    isSubmitting,
  } = props;

  const canManage = role === "sudo-admin" || role === "humanresource";

  const [form, setForm] = useState(emptyForm);
  const [openForm, setOpenForm] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // acceptance state — nghiệm thu theo từng sản phẩm cam kết (trong modal Cập nhật)
  const [paperSel, setPaperSel] = useState({}); // { [deliverableId]: paperOption }
  const [uploadingKey, setUploadingKey] = useState(null); // deliverableId đang upload minh chứng
  const [preview, setPreview] = useState(null); // url file minh chứng đang xem trước
  const [paperInfo, setPaperInfo] = useState(null); // thông tin bài báo xem (chỉ đọc)

  useEffect(() => {
    props.getAllContract();
    props.getWarnings(0);
    if (!profiles || profiles.length === 0) props.getAllProfiles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const profileName = useMemo(() => {
    const map = {};
    (profiles || []).forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [profiles]);

  // ---- Create / Edit ----
  const handleOpenInsert = () => {
    setForm(emptyForm);
    setOpenForm(true);
  };

  const handleOpenEdit = (c) => {
    setForm({
      id: c.id,
      code: c.code || "",
      title: c.title || "",
      profileId: c.profileId || "",
      startDate: toInputDate(c.startDate),
      endDate: toInputDate(c.endDate),
      acceptanceDueDate: toInputDate(c.acceptanceDueDate),
      status: c.status || "draft",
      note: c.note || "",
      deliverables: (c.deliverables || []).map((d) => ({
        id: d.id,
        productType: d.productType || "scientificResearch",
        requiredQuantity: d.requiredQuantity ?? 1,
        description: d.description || "",
      })),
    });
    // Nạp chi tiết (acceptances) + bài báo khả dụng để nghiệm thu ngay trong modal cập nhật
    setPaperSel({});
    setUploadingKey(null);
    props.getContractById(c.id);
    if (c.profileId) props.getAvailablePapers(c.profileId);
    setOpenForm(true);
  };

  const handleChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleAddDeliverable = () =>
    setForm((prev) => ({
      ...prev,
      deliverables: [...prev.deliverables, { productType: "scientificResearch", requiredQuantity: 1, description: "" }],
    }));

  const handleRemoveDeliverable = (index) =>
    setForm((prev) => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index),
    }));

  const handleDeliverableChange = (index, key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      deliverables: prev.deliverables.map((d, i) => (i === index ? { ...d, [key]: value } : d)),
    }));
  };

  const buildPayload = () => ({
    id: form.id || undefined,
    code: form.code,
    title: form.title,
    profileId: form.profileId ? Number(form.profileId) : 0,
    startDate: toIso(form.startDate),
    endDate: toIso(form.endDate),
    acceptanceDueDate: toIso(form.acceptanceDueDate),
    status: form.status,
    // [VinUni] đồng bộ cờ active/completed theo trạng thái (đã bỏ nút bật/tắt riêng)
    active: form.status === "active",
    completed: form.status === "completed",
    note: form.note,
    deliverables: (form.deliverables || []).map((d) => ({
      id: d.id || undefined,
      productType: d.productType,
      requiredQuantity: Number(d.requiredQuantity) || 0,
      description: d.description || "",
    })),
  });

  const handleSubmit = () => {
    if (!form.code.trim() || !form.title.trim()) return;
    const payload = buildPayload();
    const cleanUp = () => setOpenForm(false);
    if (form.id) props.updateContract(form.id, payload, cleanUp);
    else props.insertContract(payload, cleanUp);
  };

  const handleConfirmDelete = () => {
    if (deleting) props.deleteContract(deleting.id);
    setDeleting(null);
  };

  // ---- Acceptance (nghiệm thu) — đưa vào modal Cập nhật ----
  // Chi tiết hợp đồng đang sửa (đã lưu): chứa acceptances + deliverables
  const currentContract = useMemo(() => {
    if (!form.id) return null;
    if (detail && detail.id === form.id) return detail;
    return null;
  }, [form.id, detail]);

  // Chỉ hợp đồng đang hiệu lực mới được nghiệm thu
  const canAccept = currentContract?.status === "active";

  // Các bản ghi nghiệm thu khớp với 1 sản phẩm cam kết (theo loại sản phẩm)
  const acceptancesFor = (productType) =>
    (currentContract?.acceptances || []).filter((a) => a.productType === productType);

  // Gán 1 bài báo có sẵn cho sản phẩm cam kết
  const handleAddPaper = (deliverable) => {
    const item = paperSel[deliverable.id];
    if (!form.id || !item?.scientificResearch?.id) return;
    props.addAcceptance(form.id, {
      productType: deliverable.productType,
      scientificResearchId: Number(item.scientificResearch.id),
      title: item.scientificResearch.title,
    });
    setPaperSel((p) => ({ ...p, [deliverable.id]: null }));
  };

  // Tải minh chứng (file) cho sản phẩm cam kết
  const handleAddEvidence = async (deliverable, e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || !form.id) return;
    setUploadingKey(deliverable.id);
    const result = await props.uploadFile(form.id, "contract", [file]);
    setUploadingKey(null);
    if (result && result.success) {
      props.addAcceptance(form.id, {
        productType: deliverable.productType,
        evidenceUrl: result.data,
        title: file.name,
      });
    }
  };

  const handleDeleteAcceptance = (acc) => {
    if (form.id) props.deleteAcceptance(acc.id, form.id);
  };

  return (
    <Box style={{ padding: "16px 0" }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <Typography variant="h5">Hợp đồng TLNC (RA)</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<Sync />}
              style={{ marginRight: 8 }}
              onClick={() => {
                props.getAllContract();
                props.getWarnings(0);
              }}
            >
              Đồng bộ
            </Button>
            {canManage && (
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenInsert}>
                Thêm hợp đồng
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Banner cảnh báo */}
      {(warnings || []).length > 0 && (
        <Box mt={2}>
          <Alert severity="warning">
            {(warnings || []).map((w, i) => {
              const code = w.contract?.code || "";
              const msg =
                w.warningType === "expiring"
                  ? `HĐ ${code} sắp hết hạn còn ${w.daysLeft} ngày`
                  : `HĐ ${code} sắp đến kỳ nghiệm thu (còn ${w.daysLeft} ngày)`;
              return <div key={i}>{msg}</div>;
            })}
          </Alert>
        </Box>
      )}

      <Divider style={{ margin: "12px 0" }} />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>STT</TableCell>
              <TableCell>Số HĐ</TableCell>
              <TableCell>Tên HĐ</TableCell>
              <TableCell>Cá nhân</TableCell>
              <TableCell>Thời hạn</TableCell>
              <TableCell>Hạn nghiệm thu</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>Tiến độ NT</TableCell>
              {canManage && <TableCell align="right">Tác vụ</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={canManage ? 9 : 8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (contracts || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 9 : 8} align="center">
                  Chưa có hợp đồng.
                </TableCell>
              </TableRow>
            ) : (
              (contracts || []).map((c, index) => (
                <TableRow key={c.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{c.code}</TableCell>
                  <TableCell>{c.title}</TableCell>
                  <TableCell>{c.profile?.name || profileName[c.profileId] || ""}</TableCell>
                  <TableCell>
                    {formatDate(c.startDate)} - {formatDate(c.endDate)}
                  </TableCell>
                  <TableCell>{formatDate(c.acceptanceDueDate)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel(c.status)}
                      color={c.status === "completed" ? "primary" : c.status === "active" ? "secondary" : "default"}
                    />
                  </TableCell>
                  <TableCell>{acceptanceProgress(c)}</TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEdit(c)}
                        title="Cập nhật / Nghiệm thu"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="secondary" onClick={() => setDeleting(c)} title="Xoá">
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form thêm/sửa */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? "Cập nhật hợp đồng" : "Thêm hợp đồng"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                variant="outlined"
                label="Số hợp đồng"
                value={form.code}
                onChange={handleChange("code")}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                size="small"
                variant="outlined"
                label="Tên hợp đồng"
                value={form.title}
                onChange={handleChange("title")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={profiles || []}
                getOptionLabel={(o) => (o?.name ? `${o.name}${o.email ? ` (${o.email})` : ""}` : "")}
                getOptionSelected={(o, v) => o.id === v.id}
                value={(profiles || []).find((p) => String(p.id) === String(form.profileId)) || null}
                onChange={(_, val) => setForm((prev) => ({ ...prev, profileId: val?.id || "" }))}
                noOptionsText="Không tìm thấy cá nhân"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cá nhân"
                    variant="outlined"
                    placeholder="Tìm theo tên/email..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Trạng thái"
                value={form.status}
                onChange={handleChange("status")}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                variant="outlined"
                label="Ngày bắt đầu"
                InputLabelProps={{ shrink: true }}
                value={form.startDate}
                onChange={handleChange("startDate")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                variant="outlined"
                label="Ngày kết thúc"
                InputLabelProps={{ shrink: true }}
                value={form.endDate}
                onChange={handleChange("endDate")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                variant="outlined"
                label="Hạn nghiệm thu"
                InputLabelProps={{ shrink: true }}
                value={form.acceptanceDueDate}
                onChange={handleChange("acceptanceDueDate")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                label="Ghi chú"
                value={form.note}
                onChange={handleChange("note")}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">Sản phẩm cam kết</Typography>
                <Button size="small" startIcon={<Add />} onClick={handleAddDeliverable}>
                  Thêm sản phẩm
                </Button>
              </Box>
            </Grid>
            {(form.deliverables || []).map((d, index) => (
              <Grid item xs={12} key={index}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Loại sản phẩm"
                      value={d.productType}
                      onChange={handleDeliverableChange(index, "productType")}
                    >
                      {PRODUCT_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                      label="Số lượng"
                      value={d.requiredQuantity}
                      onChange={handleDeliverableChange(index, "requiredQuantity")}
                    />
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Mô tả"
                      value={d.description}
                      onChange={handleDeliverableChange(index, "description")}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton size="small" color="secondary" onClick={() => handleRemoveDeliverable(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
          {/* [VinUni] Nghiệm thu — chỉ với hợp đồng đã lưu & đang hiệu lực */}
          {form.id && (
            <>
              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle1" gutterBottom>
                <b>Nghiệm thu hợp đồng</b>
              </Typography>
              {!canAccept && (
                <Alert severity="warning" style={{ marginBottom: 8 }}>
                  Chỉ hợp đồng <b>đang hiệu lực</b> mới được nghiệm thu. Hãy đặt trạng thái "Đang hiệu lực" và bấm
                  Cập nhật trước.
                </Alert>
              )}
              {(currentContract?.deliverables || []).length === 0 ? (
                <Alert severity="info">
                  Chưa có sản phẩm cam kết đã lưu. Thêm sản phẩm cam kết ở trên rồi bấm Cập nhật.
                </Alert>
              ) : (
                (currentContract.deliverables || []).map((d) => {
                  const accs = acceptancesFor(d.productType);
                  const required = d.requiredQuantity || 0;
                  const done = accs.length;
                  const reached = required > 0 && done >= required;
                  return (
                    <Paper key={d.id} variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                      <Grid container spacing={2}>
                        {/* Thông tin sản phẩm cam kết */}
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle1">
                            <b>{productTypeLabel(d.productType)}</b>
                          </Typography>
                          {d.description && (
                            <Typography variant="body2" color="textSecondary">
                              {d.description}
                            </Typography>
                          )}
                          <Chip
                            size="small"
                            label={`Tiến độ: ${done}/${required}`}
                            color={reached ? "primary" : "default"}
                            style={{ marginTop: 6 }}
                          />
                        </Grid>

                        {/* Ô nghiệm thu */}
                        <Grid item xs={12} md={8}>
                          {accs.length > 0 && (
                            <Box marginBottom={1}>
                              {accs.map((acc) => (
                                <Box key={acc.id} display="flex" alignItems="center" style={{ gap: 8 }}>
                                  <AssignmentTurnedIn fontSize="small" style={{ color: "#2e548a" }} />
                                  <Typography variant="body2" style={{ flex: 1 }}>
                                    {acc.scientificResearchId
                                      ? `Bài báo: ${acc.scientificResearch?.title || acc.title || `#${acc.scientificResearchId}`}`
                                      : acc.title || "Minh chứng"}
                                  </Typography>
                                  {/* [VinUni] Xem thông tin bài báo (chỉ đọc) ngay từ màn hợp đồng */}
                                  {acc.scientificResearchId && (
                                    <Button
                                      size="small"
                                      color="primary"
                                      disabled={!acc.scientificResearch}
                                      onClick={() => setPaperInfo(buildPaperUpdating(acc))}
                                    >
                                      {acc.scientificResearch ? "Xem thông tin" : "Không có dữ liệu"}
                                    </Button>
                                  )}
                                  {acc.evidenceUrl && (
                                    <Button size="small" color="primary" onClick={() => setPreview(acc.evidenceUrl)}>
                                      Xem minh chứng
                                    </Button>
                                  )}
                                  {canManage && canAccept && (
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={() => handleDeleteAcceptance(acc)}
                                      title="Xoá"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}

                          {canManage && canAccept && (
                            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                              <Autocomplete
                                size="small"
                                style={{ minWidth: 220, flex: 1 }}
                                options={availablePapers || []}
                                getOptionLabel={(o) => o?.scientificResearch?.title || ""}
                                getOptionSelected={(o, v) => o?.scientificResearch?.id === v?.scientificResearch?.id}
                                value={paperSel[d.id] || null}
                                onChange={(_, val) => setPaperSel((p) => ({ ...p, [d.id]: val }))}
                                noOptionsText="Không có bài báo khả dụng"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Gán bài báo có sẵn"
                                    placeholder="Tìm bài báo..."
                                  />
                                )}
                              />
                              {/* [VinUni] Xem thông tin bài báo đang chọn trước khi gán (chỉ đọc) */}
                              <Button
                                color="primary"
                                disabled={!paperSel[d.id]?.scientificResearch}
                                onClick={() => setPaperInfo(buildPaperUpdating(paperSel[d.id]))}
                              >
                                Xem
                              </Button>
                              <Button
                                color="primary"
                                variant="contained"
                                disabled={!paperSel[d.id] || isSubmitting}
                                onClick={() => handleAddPaper(d)}
                              >
                                Gán
                              </Button>
                              <Typography variant="caption" color="textSecondary">
                                hoặc
                              </Typography>
                              <Button variant="outlined" component="label" disabled={uploadingKey === d.id}>
                                {uploadingKey === d.id ? "Đang tải..." : "Tải minh chứng"}
                                <input type="file" hidden onChange={(e) => handleAddEvidence(d, e)} />
                              </Button>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Đóng</Button>
          <Button color="primary" variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
            {form.id ? "Cập nhật" : "Thêm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Xác nhận xoá */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Xác nhận xoá</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc muốn xoá hợp đồng <b>{deleting?.code}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Huỷ</Button>
          <Button color="secondary" variant="contained" onClick={handleConfirmDelete}>
            Xoá
          </Button>
        </DialogActions>
      </Dialog>

      {/* [VinUni] Xem trước file minh chứng */}
      <FilePreviewModal open={!!preview} url={preview} handleClose={() => setPreview(null)} />

      {/* [VinUni] Xem thông tin bài báo bằng CHÍNH form khai báo, ở chế độ chỉ đọc */}
      <ScientificResearchUpdateModal
        readOnly
        adminTab={false}
        other={false}
        dialogStyle={{ zIndex: 2000 }} /* [VinUni] nổi trên modal hợp đồng */
        filter={paperFilterFromType(paperInfo?.type)}
        type={paperInfo?.type}
        updating={paperInfo}
        setUpdating={setPaperInfo}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  contracts: select(state, "raContractReducer", "list"),
  detail: select(state, "raContractReducer", "detail"),
  availablePapers: select(state, "raContractReducer", "availablePapers"),
  warnings: select(state, "raContractReducer", "warnings"),
  isFetching: select(state, "raContractReducer", "isFetching"),
  isSubmitting: select(state, "raContractReducer", "isSubmitting"),
  profiles: select(state, "departmentReducer", "allProfiles"),
});

const mapDispatchToProps = (dispatch) => ({
  getAllContract: () => dispatch(getAllContract()),
  getContractById: (id) => dispatch(getContractById(id)),
  getAllProfiles: () => dispatch(getAllProfiles()),
  insertContract: (payload, cleanUp) => dispatch(insertContract(payload, cleanUp)),
  updateContract: (id, payload, cleanUp) => dispatch(updateContract(id, payload, cleanUp)),
  deleteContract: (id) => dispatch(deleteContract(id)),
  addAcceptance: (contractId, payload, cleanUp) => dispatch(addAcceptance(contractId, payload, cleanUp)),
  deleteAcceptance: (acceptanceId, contractId) => dispatch(deleteAcceptance(acceptanceId, contractId)),
  getAvailablePapers: (profileId) => dispatch(getAvailablePapers(profileId)),
  getWarnings: (profileId) => dispatch(getWarnings(profileId)),
  uploadFile: (id, fileType, files) => dispatch(uploadFile(id, fileType, files)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(RaContract));
